import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import termsMarkdownUrl from "../content/legal/terms-and-conditions.md";
import privacyMarkdownUrl from "../content/legal/privacy-and-user-consent.md";
import attributionsMarkdownUrl from "../content/legal/api-and-data-attributions.md";
import accountDeletionMarkdownUrl from "../content/legal/account-deletion.md";
import "../styles/pages/LegalDocumentPage.css";

const LEGAL_DOCUMENTS = {
  terms: {
    titleKey: "legal_document_terms_title",
    path: "/legal/terms",
    url: termsMarkdownUrl,
  },
  privacy: {
    titleKey: "legal_document_privacy_title",
    path: "/legal/privacy",
    url: privacyMarkdownUrl,
  },
  accountDeletion: {
    titleKey: "legal_document_account_deletion_title",
    path: "/legal/account-deletion",
    url: accountDeletionMarkdownUrl,
  },
  attributions: {
    titleKey: "legal_document_attributions_title",
    path: "/legal/attributions",
    url: attributionsMarkdownUrl,
  },
};

export default function LegalDocumentPage({ documentKey, fallbackPath = "/" }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const documentConfig =
    LEGAL_DOCUMENTS[documentKey] || LEGAL_DOCUMENTS.terms;
  const documentTitle = t(documentConfig.titleKey);
  const [markdown, setMarkdown] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    setIsLoading(true);
    setError("");

    fetch(documentConfig.url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load ${documentTitle}`);
        }
        return response.text();
      })
      .then((text) => {
        if (isMounted) {
          setMarkdown(text);
        }
      })
      .catch((loadError) => {
        console.error("Legal markdown load error:", loadError);
        if (isMounted) {
          setError(t("legal_document_load_error"));
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [documentConfig, documentTitle, t]);

  const markdownComponents = useMemo(
    () => ({
      a: ({ href = "", children, ...props }) => {
        const isExternal = /^https?:\/\//i.test(href);
        return (
          <a
            href={href}
            target={isExternal ? "_blank" : undefined}
            rel={isExternal ? "noopener noreferrer" : undefined}
            {...props}
          >
            {children}
          </a>
        );
      },
    }),
    []
  );

  const handleClose = () => {
    const historyIndex =
      typeof window !== "undefined" ? window.history.state?.idx : 0;

    if (Number.isFinite(historyIndex) && historyIndex > 0) {
      navigate(-1);
      return;
    }

    navigate(fallbackPath, { replace: true });
  };

  return (
    <div className="legal-page-shell">
      <div className="legal-page">
        <button
          type="button"
          className="legal-page-close"
          onClick={handleClose}
          aria-label={t("legal_document_close", { defaultValue: "Close legal document" })}
          title={t("legal_document_close", { defaultValue: "Close legal document" })}
        >
          <X size={20} strokeWidth={2.4} aria-hidden="true" />
        </button>
        <div className="legal-page-header">
          <p className="legal-page-eyebrow">{t("legal_page_label")}</p>
          <div
            className="legal-page-links"
            aria-label={t("legal_documents_label")}
          >
            {Object.entries(LEGAL_DOCUMENTS).map(([key, item]) => (
              <Link
                key={key}
                to={item.path}
                className={`legal-page-link ${
                  key === documentKey ? "active" : ""
                }`}
              >
                {t(item.titleKey)}
              </Link>
            ))}
          </div>
        </div>

        <div className="legal-page-content">
          {isLoading ? (
            <p className="legal-page-state">{t("legal_document_loading")}</p>
          ) : error ? (
            <p className="legal-page-state legal-page-state-error">{error}</p>
          ) : (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={markdownComponents}
            >
              {markdown}
            </ReactMarkdown>
          )}
        </div>
      </div>
    </div>
  );
}
